import { useEffect, useRef, useState } from 'react';
import type { DiligenceTemplateSummary } from '../types';
import { listDiligenceTemplatePage } from '../services/diligenceTemplateService';

/** Only explicit pagination expands the library. Refresh retains the opened pages. */
export function useTemplateLibraryPages(isOpen: boolean, refreshKey: number) {
  const [templates, setTemplates] = useState<DiligenceTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const pages = useRef(1);
  const currentRun = useRef({ cancelled: false });
  const busy = useRef(false);

  useEffect(() => {
    const run = { cancelled: false };
    currentRun.current = run;
    busy.current = true;
    setLoadingMore(false);
    if (!isOpen) {
      pages.current = 1;
      setTemplates([]);
      setNextCursor(null);
      setLoading(true);
      setPageError(null);
      busy.current = false;
      return;
    }
    setLoading(true);
    setPageError(null);
    void (async () => {
      let cursor: string | undefined;
      const items = new Map<string, DiligenceTemplateSummary>();
      let loadedPages = 0;
      do {
        const result = await listDiligenceTemplatePage(cursor);
        if (run.cancelled) return;
        result.templates.forEach(item => items.set(item.id, item));
        cursor = result.nextCursor || undefined;
        loadedPages++;
      } while (cursor && loadedPages < pages.current);
      pages.current = loadedPages;
      setTemplates([...items.values()]);
      setNextCursor(cursor || null);
    })().catch(() => {
      if (!run.cancelled) setPageError('No se pudo cargar la biblioteca.');
    }).finally(() => {
      if (!run.cancelled) { busy.current = false; setLoading(false); }
    });
    return () => { run.cancelled = true; };
  }, [isOpen, refreshKey]);

  const loadMore = async () => {
    if (!isOpen || !nextCursor || busy.current) return;
    const run = currentRun.current;
    busy.current = true;
    setLoadingMore(true);
    setPageError(null);
    try {
      const result = await listDiligenceTemplatePage(nextCursor);
      if (run.cancelled) return;
      setTemplates(previous => [...new Map([...previous, ...result.templates].map(item => [item.id, item])).values()]);
      setNextCursor(result.nextCursor);
      pages.current++;
    } catch {
      if (!run.cancelled) setPageError('No se pudieron cargar más plantillas. Inténtalo de nuevo.');
    } finally {
      if (!run.cancelled) { busy.current = false; setLoadingMore(false); }
    }
  };
  return { templates, loading, loadingMore, pageError, hasMore: !!nextCursor, loadMore };
}
